// src/pages/ELearning/TaskBenefitsList.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import {
  Plus, Loader2, AlertCircle, Clock, Award, CheckCircle, XCircle,
  Play, FileText, User, Calendar, Edit, Trash2, Eye, Upload
} from 'lucide-react';

interface TaskBenefit {
  _id: string;
  title: string;
  description: string;
  points: number;
  deadline: string;
  status: 'open' | 'in_progress' | 'completed' | 'approved' | 'rejected';
  image?: string;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  assignedTo?: {
    _id: string;
    name: string;
    email: string;
  };
  report?: string;
  completedAt?: string;
  createdAt: string;
}

const TaskBenefitsList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [tasks, setTasks] = useState<TaskBenefit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [reportModal, setReportModal] = useState<{
    open: boolean;
    taskId: string;
    report: string;
    file: File | null;
  }>({
    open: false,
    taskId: '',
    report: '',
    file: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const res = await api.get('/elearning/task-benefits');
      setTasks(res.data);
    } catch (err: any) {
      console.error('Ошибка загрузки задач:', err);
      setError(err.response?.data?.error || 'Не удалось загрузить задачи');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleTakeTask = async (taskId: string) => {
    try {
      await api.post(`/elearning/task-benefits/${taskId}/take`);
      showToast('Задача взята в работу', 'success');
      fetchTasks();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось взять задачу', 'error');
    }
  };

  const handleSubmitReport = async () => {
    if (!reportModal.report.trim() && !reportModal.file) {
      showToast('Введите отчёт или прикрепите файл', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('report', reportModal.report);
      if (reportModal.file) {
        formData.append('file', reportModal.file);
      }

      await api.post(`/elearning/task-benefits/${reportModal.taskId}/report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast('Отчёт отправлен', 'success');
      setReportModal({ open: false, taskId: '', report: '', file: null });
      fetchTasks();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось отправить отчёт', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveTask = async (taskId: string) => {
    try {
      await api.post(`/elearning/task-benefits/${taskId}/approve`);
      showToast('Задача одобрена, баллы начислены', 'success');
      fetchTasks();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось одобрить задачу', 'error');
    }
  };

  const handleRejectTask = async (taskId: string) => {
    try {
      await api.post(`/elearning/task-benefits/${taskId}/reject`);
      showToast('Задача отклонена', 'success');
      fetchTasks();
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Не удалось отклонить задачу', 'error');
    }
  };

  const handleDeleteTask = (taskId: string) => {
    confirm.showConfirm({
      title: 'Удаление задачи',
      message: 'Вы уверены, что хотите удалить эту задачу?',
      confirmText: 'Удалить',
      onConfirm: async () => {
        try {
          await api.delete(`/elearning/task-benefits/${taskId}`);
          showToast('Задача удалена', 'success');
          fetchTasks();
        } catch (err: any) {
          showToast(err.response?.data?.error || 'Не удалось удалить задачу', 'error');
        }
      },
    });
  };

  const filteredTasks = tasks.filter(
    (t) => filterStatus === 'all' || t.status === filterStatus
  );

  const getStatusBadge = (status: string) => {
    const map: Record<string, { label: string; color: string; bg: string }> = {
      open: { label: 'Открыта', color: '#2575fc', bg: 'rgba(37,117,252,0.1)' },
      in_progress: { label: 'В работе', color: '#faad14', bg: 'rgba(250,173,20,0.1)' },
      completed: { label: 'Выполнена', color: '#52c41a', bg: 'rgba(82,196,26,0.1)' },
      approved: { label: 'Одобрена', color: '#2ecc71', bg: 'rgba(46,204,113,0.1)' },
      rejected: { label: 'Отклонена', color: '#ff4d4f', bg: 'rgba(255,77,79,0.1)' },
    };
    const s = map[status] || { label: status, color: '#666', bg: '#f0f0f0' };
    return (
      <span
        style={{
          background: s.bg,
          color: s.color,
          padding: '4px 10px',
          borderRadius: 20,
          fontSize: 12,
          fontWeight: 500,
        }}
      >
        {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <AlertCircle size={48} color="#ff4d4f" />
          <h2>{error}</h2>
          <button onClick={fetchTasks} style={styles.retryButton}>
            Повторить
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.06); } }
        .tb-hero { animation: fadeUp 0.5s ease-out; }
        .tb-card { transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1); animation: fadeUp 0.5s ease-out backwards; }
        .tb-card:hover { transform: translateY(-6px); box-shadow: 0 20px 50px rgba(31, 38, 135, 0.15) !important; }
        .tb-btn { transition: all 0.3s ease; }
        .tb-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4) !important; }
        .tb-chip { transition: all 0.25s ease; }
        .tb-chip:hover { transform: translateY(-2px); }
        .tb-icon-btn { transition: all 0.2s; border-radius: 10px; }
        .tb-icon-btn:hover { background: rgba(0,0,0,0.06); }
      `}</style>

      {/* ═══ Hero ═══ */}
      <div className="tb-hero" style={styles.hero}>
        <div style={styles.heroIcon}>
          <FileText size={28} color="#fff" />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={styles.title}>Задачи с бенефитами</h1>
          <p style={styles.subtitle}>Выполняйте задачи и зарабатывайте баллы</p>
        </div>
        <button
          onClick={() => navigate('/app/elearning/task-benefits/new')}
          className="tb-btn"
          style={styles.createButton}
        >
          <Plus size={18} /> Создать задачу
        </button>
      </div>

      {/* ═══ Filter chips ═══ */}
      <div style={styles.chipsRow}>
        {[
          { value: 'all', label: '📋 Все', count: tasks.length },
          { value: 'open', label: '🟢 Открытые', count: tasks.filter(t => t.status === 'open').length },
          { value: 'in_progress', label: '🟡 В работе', count: tasks.filter(t => t.status === 'in_progress').length },
          { value: 'completed', label: '🔵 Выполнено', count: tasks.filter(t => t.status === 'completed').length },
          { value: 'approved', label: '✅ Одобрено', count: tasks.filter(t => t.status === 'approved').length },
          { value: 'rejected', label: '❌ Отклонено', count: tasks.filter(t => t.status === 'rejected').length },
        ].map(c => (
          <button
            key={c.value}
            onClick={() => setFilterStatus(c.value)}
            className="tb-chip"
            style={{
              ...styles.chip,
              ...(filterStatus === c.value ? styles.chipActive : {}),
            }}
          >
            {c.label}
            <span style={{
              marginLeft: 8,
              padding: '1px 8px',
              borderRadius: 10,
              background: filterStatus === c.value ? 'rgba(255,255,255,0.25)' : 'rgba(102,126,234,0.12)',
              fontSize: 11,
              fontWeight: 700,
            }}>
              {c.count}
            </span>
          </button>
        ))}
      </div>

      {/* ═══ Grid ═══ */}
      {filteredTasks.length === 0 ? (
        <div style={styles.empty}>
          <FileText size={56} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 16, fontWeight: 700 }}>
            Нет задач
          </h3>
          <p style={{ color: '#8b8fa8', fontSize: 14 }}>
            {filterStatus === 'all' ? 'Создайте первую задачу' : 'Попробуйте другой фильтр'}
          </p>
        </div>
      ) : (
        <div style={styles.grid}>
          {filteredTasks.map((task, idx) => {
            const isCreator = task.createdBy._id === user?.id;
            const isAssignee = task.assignedTo?._id === user?.id;
            // ★ Admin компании тоже может управлять задачами
            const isAdmin = ['admin', 'superadmin'].includes(user?.role || '');
            const canTake = task.status === 'open' && !isCreator;
            const canReport = task.status === 'in_progress' && isAssignee;
            const canApprove = task.status === 'completed' && (isCreator || isAdmin);
            const canDelete = (isCreator || isAdmin) && ['open', 'rejected', 'completed', 'approved'].includes(task.status);

            const deadline = new Date(task.deadline);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            deadline.setHours(0, 0, 0, 0);
            const daysLeft = Math.round((deadline.getTime() - today.getTime()) / 86400000);
            const isOverdue = daysLeft < 0 && !['completed', 'approved'].includes(task.status);
            const isUrgent = daysLeft >= 0 && daysLeft <= 3 && !['completed', 'approved'].includes(task.status);

            return (
              <div
                key={task._id}
                className="tb-card"
                style={{ ...styles.card, animationDelay: `${idx * 0.05}s` }}
              >
                {/* Top gradient strip по статусу */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: task.status === 'approved'
                    ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
                    : task.status === 'completed'
                    ? 'linear-gradient(135deg, #2575fc 0%, #6a11cb 100%)'
                    : task.status === 'in_progress'
                    ? 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)'
                    : task.status === 'rejected'
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }} />

                {task.image && (
                  <div style={styles.imageWrapper}>
                    <img src={task.image} alt={task.title} style={styles.cardImage} />
                  </div>
                )}

                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>{task.title}</h3>
                  {getStatusBadge(task.status)}
                </div>

                <p style={styles.cardDesc}>{task.description}</p>

                {/* Deadline индикатор */}
                <div style={{
                  padding: '8px 12px',
                  borderRadius: 10,
                  marginBottom: 12,
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: isOverdue
                    ? 'rgba(239, 68, 68, 0.1)'
                    : isUrgent
                    ? 'rgba(245, 158, 11, 0.1)'
                    : 'rgba(102, 126, 234, 0.08)',
                  color: isOverdue ? '#ef4444' : isUrgent ? '#f59e0b' : '#667eea',
                }}>
                  <Calendar size={13} />
                  {isOverdue
                    ? `⚠️ Просрочено на ${Math.abs(daysLeft)} дн.`
                    : daysLeft === 0
                    ? '🔥 Сегодня последний день'
                    : `⏰ ${daysLeft} дн. до ${new Date(task.deadline).toLocaleDateString('ru-RU')}`}
                </div>

                <div style={styles.cardMeta}>
                  <span style={styles.pointsBadge}>
                    <Award size={13} /> {task.points} баллов
                  </span>
                </div>

                <div style={styles.cardPeople}>
                  <div style={styles.personRow}>
                    <div style={styles.miniAvatar}>
                      {(task.createdBy.name || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <span>{task.createdBy.name}</span>
                  </div>
                  {task.assignedTo && (
                    <div style={styles.personRow}>
                      <div style={{...styles.miniAvatar, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'}}>
                        {(task.assignedTo.name || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <span>{task.assignedTo.name}</span>
                    </div>
                  )}
                </div>

                {task.report && (
                  <div style={styles.reportBlock}>
                    <FileText size={13} style={{ flexShrink: 0 }} />
                    <span>{task.report.slice(0, 120)}{task.report.length > 120 ? '…' : ''}</span>
                  </div>
                )}

                <div style={styles.cardActions}>
                  {canTake && (
                    <button onClick={() => handleTakeTask(task._id)} className="tb-btn" style={styles.actionButton}>
                      <Play size={14} /> Взять в работу
                    </button>
                  )}
                  {canReport && (
                    <button
                      onClick={() => setReportModal({ open: true, taskId: task._id, report: '', file: null })}
                      className="tb-btn"
                      style={styles.actionButton}
                    >
                      <FileText size={14} /> Отправить отчёт
                    </button>
                  )}
                  {canApprove && (
                    <>
                      <button
                        onClick={() => handleApproveTask(task._id)}
                        className="tb-btn"
                        style={{ ...styles.actionButton, background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' }}
                      >
                        <CheckCircle size={14} /> Одобрить
                      </button>
                      <button
                        onClick={() => handleRejectTask(task._id)}
                        style={{ ...styles.actionButton, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}
                      >
                        <XCircle size={14} /> Отклонить
                      </button>
                    </>
                  )}
                  {(isCreator || isAdmin) && task.status === 'open' && (
                    <button
                      onClick={() => navigate(`/app/elearning/task-benefits/${task._id}/edit`)}
                      className="tb-icon-btn"
                      style={styles.iconButton}
                    >
                      <Edit size={16} color="#667eea" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDeleteTask(task._id)} className="tb-icon-btn" style={styles.iconButton}>
                      <Trash2 size={16} color="#ef4444" />
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/app/elearning/task-benefits/${task._id}`)}
                    className="tb-icon-btn"
                    style={styles.iconButton}
                  >
                    <Eye size={16} color="#667eea" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ Модалка отчёта ═══ */}
      {reportModal.open && (
        <div
          style={modalStyles.overlay}
          onClick={() => setReportModal({ open: false, taskId: '', report: '', file: null })}
        >
          <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={modalStyles.modalHeader}>
              <div style={modalStyles.modalIcon}>
                <FileText size={22} color="#fff" />
              </div>
              <h3 style={modalStyles.title}>Отправить отчёт о выполнении</h3>
            </div>

            <textarea
              placeholder="Опишите, что было сделано..."
              value={reportModal.report}
              onChange={(e) => setReportModal({ ...reportModal, report: e.target.value })}
              style={modalStyles.textarea}
              rows={5}
            />
            <div style={modalStyles.fileRow}>
              <button onClick={() => fileInputRef.current?.click()} style={modalStyles.fileButton}>
                <Upload size={16} /> {reportModal.file ? 'Заменить файл' : 'Выберите файл'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setReportModal({ ...reportModal, file });
                }}
                style={{ display: 'none' }}
              />
              {reportModal.file && (
                <span style={modalStyles.fileName}>📎 {reportModal.file.name}</span>
              )}
            </div>
            <div style={modalStyles.actions}>
              <button
                onClick={handleSubmitReport}
                disabled={submitting}
                className="tb-btn"
                style={modalStyles.primaryButton}
              >
                {submitting ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <><CheckCircle size={16} /> Отправить</>
                )}
              </button>
              <button
                onClick={() => setReportModal({ open: false, taskId: '', report: '', file: null })}
                style={modalStyles.secondaryButton}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  hero: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  heroIcon: { width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #2575fc 0%, #6a11cb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(37, 117, 252, 0.4)', flexShrink: 0 },
  title: { fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #2575fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', letterSpacing: '-0.5px' },
  subtitle: { color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 },
  createButton: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '12px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)' },
  chipsRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 },
  chip: { padding: '8px 14px', background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.6)', borderRadius: 14, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#5a5f7a', display: 'inline-flex', alignItems: 'center' },
  chipActive: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', border: '1px solid transparent', boxShadow: '0 6px 20px rgba(102, 126, 234, 0.35)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 },
  empty: { textAlign: 'center', padding: 80, background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(20px)', borderRadius: 24, border: '1px dashed rgba(102,126,234,0.3)' },
  card: { position: 'relative', background: 'rgba(255, 255, 255, 0.55)', backdropFilter: 'blur(24px) saturate(180%)', WebkitBackdropFilter: 'blur(24px) saturate(180%)', borderRadius: 24, padding: 20, border: '1px solid rgba(255, 255, 255, 0.7)', boxShadow: '0 8px 32px rgba(31, 38, 135, 0.08)', wordBreak: 'break-word', overflow: 'hidden' },
  imageWrapper: { marginBottom: 12, textAlign: 'center' },
  cardImage: { maxWidth: '100%', maxHeight: 120, objectFit: 'cover', borderRadius: 12 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 8, flexWrap: 'wrap' },
  cardTitle: { fontSize: 17, fontWeight: 700, color: '#1a1a2e', margin: 0, flex: 1, lineHeight: 1.3 },
  cardDesc: { fontSize: 13, color: '#5a5f7a', marginBottom: 14, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' },
  cardMeta: { display: 'flex', gap: 12, marginBottom: 12 },
  pointsBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', color: '#fff', borderRadius: 12, fontSize: 12, fontWeight: 800, boxShadow: '0 4px 12px rgba(246, 211, 101, 0.4)' },
  cardPeople: { display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12, fontSize: 12, color: '#5a5f7a' },
  personRow: { display: 'flex', alignItems: 'center', gap: 8 },
  miniAvatar: { width: 22, height: 22, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0 },
  reportBlock: { display: 'flex', alignItems: 'flex-start', gap: 8, background: 'rgba(102, 126, 234, 0.06)', padding: 12, borderRadius: 12, fontSize: 12, color: '#5a5f7a', marginBottom: 14, lineHeight: 1.5 },
  cardActions: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, alignItems: 'center' },
  actionButton: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 12, padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, boxShadow: '0 4px 12px rgba(102, 126, 234, 0.25)' },
  iconButton: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  retryButton: { marginTop: 20, padding: '10px 24px', background: '#2575fc', color: 'white', border: 'none', borderRadius: 30, cursor: 'pointer', fontWeight: 600 }
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: 16,
    animation: 'fadeUp 0.3s ease-out',
  },
  modal: {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    borderRadius: 24,
    padding: 28,
    width: 520,
    maxWidth: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    boxShadow: '0 24px 64px rgba(31, 38, 135, 0.2)',
  },
  modalHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  modalIcon: { width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(102, 126, 234, 0.35)', flexShrink: 0 },
  title: { fontSize: 18, fontWeight: 700, margin: 0, color: '#1a1a2e' },
  textarea: { width: '100%', padding: 14, borderRadius: 14, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 16, resize: 'vertical', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  fileRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  fileButton: { background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(4px)', border: '1.5px dashed rgba(102,126,234,0.4)', borderRadius: 14, padding: '10px 16px', color: '#667eea', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, transition: 'all 0.2s' },
  fileName: { fontSize: 13, color: '#5a5f7a', wordBreak: 'break-word', fontWeight: 500 },
  actions: { display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' },
  primaryButton: { background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: 14, padding: '12px 22px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 14, boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)' },
  secondaryButton: { background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '12px 22px', color: '#5a5f7a', cursor: 'pointer', fontWeight: 600, fontSize: 14 },
};

export default TaskBenefitsList;